/** Installed-PWA detection + service worker/push helpers. */

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) ||
    nav.standalone === true
  );
}

/**
 * Events whose standalone launch was already reported during THIS app launch.
 * Reported on every launch, not once per event for ever (as until 2026-10-16):
 * the report solves the event's install quest, and a player who installed
 * before that quest existed — or before its window opened — must be solved on
 * a later launch. The server treats repeats as no-ops.
 */
const reportedThisLaunch = new Set<string>();

/** Report the standalone launch for this event? Once per launch, only when installed. */
export function shouldReportInstall(eventId: string): boolean {
  return isStandalone() && !reportedThisLaunch.has(eventId);
}

export function markInstallReported(eventId: string): void {
  reportedThisLaunch.add(eventId);
}

/** Tests only. */
export function resetInstallReportsForTest(): void {
  reportedThisLaunch.clear();
}

// ---------------------------------------------------------------- install prompt

/** Chromium's install prompt event (not in the DOM lib types). */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<() => void>();

/**
 * Keep the browser's install prompt for later. Chromium fires
 * `beforeinstallprompt` once, early and unasked; whoever is not listening at
 * that moment never gets it — so this runs at app start (main.tsx), and the
 * install quest's button calls `promptInstall()` when the player taps it.
 */
export function captureInstallPrompt(target: Window = window): void {
  target.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault(); // no mini-infobar; we offer the button ourselves
    deferredPrompt = event as BeforeInstallPromptEvent;
    for (const listener of promptListeners) listener();
  });
  target.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    for (const listener of promptListeners) listener();
  });
}

export function canPromptInstall(): boolean {
  return deferredPrompt !== null;
}

/** Subscribe to prompt availability changes; returns the unsubscribe function. */
export function onInstallPromptChange(listener: () => void): () => void {
  promptListeners.add(listener);
  return () => promptListeners.delete(listener);
}

/** Show the browser's own install dialog. A prompt can be used once. */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const prompt = deferredPrompt;
  if (!prompt) return "unavailable";
  deferredPrompt = null;
  for (const listener of promptListeners) listener();
  await prompt.prompt();
  return (await prompt.userChoice).outcome;
}

/** Base64url VAPID key → Uint8Array for pushManager.subscribe. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** Whether this browser can do web push at all (iOS: only the installed app). */
export function pushSupported(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export interface PushSubscriptionInput {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  userAgent?: string;
}

/**
 * Ask permission + subscribe this device (spec §5: prompt only when running
 * as installed PWA — callers gate on isStandalone()).
 */
export async function subscribeToPush(vapidPublicKey: string): Promise<PushSubscriptionInput | null> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return null;
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
  });
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return null;
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    userAgent: navigator.userAgent,
  };
}

/**
 * The subscription this device already has, if permission was granted before —
 * re-sent silently on launch (deduped by endpoint server-side), which is how a
 * player who turned notifications on before the event had a notifications quest
 * gets it solved.
 */
export async function existingPushSubscription(): Promise<PushSubscriptionInput | null> {
  if (!pushSupported() || Notification.permission !== "granted") return null;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  const json = subscription?.toJSON();
  if (!json?.endpoint || !json.keys?.p256dh || !json.keys.auth) return null;
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }, userAgent: navigator.userAgent };
}
